/* ============================================================================
   FORMES — geometria de las zonas de impresion
   ----------------------------------------------------------------------------
   Hasta ahora una zona era siempre un rectangulo y bastaban x/y/w/h. Los
   porte-cles obligan a soportar circulo, corazon y hexagono (ver PRODUITS.md).

   Todo se define UNA vez como path normalizado en la caja 0..1 y luego se
   escala a la caja px de la zona. Asi el resto del Studio no necesita saber
   que forma es: pide un path, un clip o un test de contencion.

   Zone de securite: para el rectangulo se mantiene el margen proporcional de
   siempre (4% por lado). Para las demas formas se ESCALA el mismo path hacia
   el centro, que es lo que especifica PRODUITS.md ("el mismo path al ~86%").
   Un offset real de curva daria un contorno mas fiel pero es mucho mas caro y
   la diferencia es invisible a estos tamanos.

   Agujeros (porte-cles): la anilla no se imprime. Se declara en cm y se
   excluye de la zona de securite, ademas de dibujarse sobre el mockup para
   que el cliente vea donde esta.
   ========================================================================== */

const FORMES = {
  /* El rectangulo no usa path: se dibuja como <rect> para conservar el radio
     de esquinas opcional y el aspecto exacto de siempre. */
  rect: {
    securiteDefecto: null,          // usa defauts().zoneSecurite
  },

  /* El cuadrado TAMPOCO usa path, y no es un descuido: un cuadrado es un
     rectangulo con los dos lados iguales, asi que dibujarlo, recortarlo y
     preguntar si un punto cae dentro es exactamente lo mismo. Lo unico que
     cambia es que el admin le mantiene los lados iguales EN CENTIMETROS, que
     es lo que se imprime. Si tuviera su propio path, la zone de securite y el
     radio de esquinas dejarian de funcionar como en el rectangulo, sin ganar
     nada a cambio. */
  carre: {
    securiteDefecto: null,          // igual que el rectangulo
  },

  cercle: {
    securiteDefecto: 0.88,          // PRODUITS.md: 4,4 sobre 5 cm
    path: 'M 0.5 0 A 0.5 0.5 0 1 1 0.499 0 Z',
  },

  coeur: {
    securiteDefecto: 0.86,
    path: 'M 0.5 1 C 0.5 1 0 0.66 0 0.3 C 0 0.12 0.14 0 0.3 0 '
        + 'C 0.4 0 0.48 0.06 0.5 0.12 C 0.52 0.06 0.6 0 0.7 0 '
        + 'C 0.86 0 1 0.12 1 0.3 C 1 0.66 0.5 1 0.5 1 Z',
  },

  /* Hexagono con vertice arriba: deja material en el centro superior, que es
     donde va la anilla. */
  hexagone: {
    securiteDefecto: 0.86,
    path: 'M 0.5 0 L 1 0.25 L 1 0.75 L 0.5 1 L 0 0.75 L 0 0.25 Z',
    /* Los mismos seis vertices sueltos, en fracciones 0..1 de la caja. El
       admin los usa como punto de partida de las seis asas. */
    sommets: [[0.5,0], [1,0.25], [1,0.75], [0.5,1], [0,0.75], [0,0.25]],
  },
};

const formeDe = z => (z && z.forme) || 'rect';

/* --- Vertices sueltos -----------------------------------------------------
   Un hexagono FOTOGRAFIADO no es el hexagono perfecto del path: esta girado,
   en escorzo, y sus seis lados no miden lo mismo en la imagen. Con solo las
   cuatro esquinas de la caja no hay manera de hacerlo coincidir. Franklin:
   « para los hexagonos tienes que ponerme seis puntos para poder moverlo y
   centrarlos, porque si no, no me conviene ».

   Por eso una zona puede llevar sus PROPIOS vertices, en fracciones 0..1 de
   su caja. Mandan sobre el path de la familia, y no hay que tocar nada mas:
   el clip, la contencion y la zone de securite salen solos, porque los tres
   pasan por pathZone().

   Se quedan dentro de la caja a proposito. La caja es lo que traduce a
   centimetros; si un vertice pudiera salirse, el tamano impreso dejaria de
   corresponder con lo declarado — que es el fallo que costo once productos
   mal calibrados. Para agrandar la forma se agranda la caja. */
function sommetsDe(z) {
  const s = z && z.sommets;
  return (Array.isArray(s) && s.length >= 3 && s.every(
    p => Array.isArray(p) && p.length === 2 && isFinite(p[0]) && isFinite(p[1])
  )) ? s : null;
}
/* Los de la familia: lo que se dibuja mientras la zona no tenga los suyos. */
function sommetsFamille(z) {
  const f = FORMES[formeDe(z)];
  return (f && f.sommets) ? f.sommets : null;
}
/* Los que hay que ENSENAR como asas: los propios, o los de la familia. */
const sommetsVisibles = z => sommetsDe(z) || sommetsFamille(z);

/* Una zona con vertices propios ya no es un rectangulo aunque su familia lo
   sea: tiene que pasar por el path como las demas. */
const CAJAS_RECTAS = ['rect', 'carre'];
const esRect  = z => CAJAS_RECTAS.includes(formeDe(z)) && !sommetsDe(z);

/* --- Rotacion de la zona --------------------------------------------------
   La rotacion es VISUAL: coloca la zona sobre un mockup fotografiado en
   angulo (el hexagono apoyado, por ejemplo). No afecta al archivo de
   impresion, que sale siempre derecho, ni al contrato del JSON, cuyas
   coordenadas siguen siendo 0..1 relativas a la zona.

   Todo gira alrededor del CENTRO de la zona. Para preguntar si un punto cae
   dentro basta con girarlo en sentido contrario y comparar con la zona sin
   rotar: mas barato y exacto que rotar la forma. */
const rotacionDe = z => (z && +z.rotation) || 0;

const centroDe = z => ({ cx: z.x + z.w / 2, cy: z.y + z.h / 2 });

/* Lleva un punto del espacio del mockup al espacio LOCAL de la zona. */
function aLocal(z, x, y) {
  const a = rotacionDe(z);
  if (!a) return { x, y };
  const { cx, cy } = centroDe(z);
  const r = -a * Math.PI / 180, cos = Math.cos(r), sin = Math.sin(r);
  const dx = x - cx, dy = y - cy;
  return { x: cx + dx * cos - dy * sin, y: cy + dx * sin + dy * cos };
}

/* Factor de la zone de securite para una zona concreta.
   Devuelve la fraccion del tamano original que ocupa (0..1). */
function facteurSecurite(z, defectoRect) {
  if (z && typeof z.securite === 'number') return z.securite;
  const f = FORMES[formeDe(z)];
  if (f && f.securiteDefecto != null) return f.securiteDefecto;
  return 1 - 2 * (defectoRect != null ? defectoRect : 0.04);   // rect: margen por lado
}

/* --- Path en coordenadas absolutas del mockup ---------------------------- */
/* El path normalizado se transforma token a token: una sustitucion por regex
   no distingue las X de las Y ni los flags del comando de arco. */
function transformarPath(d, x0, y0, w, h) {
  const tokens = d.match(/[a-zA-Z]|-?\d*\.?\d+(?:e-?\d+)?/g) || [];
  const salida = [];
  let cmd = '';
  let i = 0;

  // cuantos numeros consume cada comando, y cuales son coordenadas
  const firma = {
    M: ['x','y'], L: ['x','y'], T: ['x','y'],
    C: ['x','y','x','y','x','y'], S: ['x','y','x','y'], Q: ['x','y','x','y'],
    H: ['x'], V: ['y'],
    A: ['rx','ry','rot','flag','flag','x','y'],
    Z: [],
  };

  while (i < tokens.length) {
    const t = tokens[i];
    if (/[a-zA-Z]/.test(t)) { cmd = t.toUpperCase(); salida.push(t); i++; continue; }
    const tipos = firma[cmd] || [];
    for (let k = 0; k < tipos.length && i < tokens.length; k++) {
      const v = parseFloat(tokens[i]);
      let r;
      switch (tipos[k]) {
        case 'x':    r = x0 + v * w; break;
        case 'y':    r = y0 + v * h; break;
        case 'rx':   r = v * w;      break;
        case 'ry':   r = v * h;      break;
        default:     r = v;          break;   // rot y flags intactos
      }
      salida.push(Math.round(r * 1000) / 1000);
      i++;
    }
    if (!tipos.length) i++;   // Z u orden desconocida
  }
  return salida.join(' ');
}

/* API real: path absoluto de la forma de la zona a la escala pedida. */
function pathZone(z, escala) {
  const s = (escala == null) ? 1 : escala;
  const w = z.w * s, h = z.h * s;
  const x0 = z.x + (z.w - w) / 2, y0 = z.y + (z.h - h) / 2;

  /* Vertices propios: un poligono cerrado, y se acabo. La escala de la zone
     de securite ya viene aplicada en w/h, asi que encoge hacia el centro de
     la caja igual que en las demas formas. */
  const pts = sommetsDe(z);
  if (pts) {
    const r = v => Math.round(v * 1000) / 1000;
    return 'M ' + pts.map(([px, py]) => `${r(x0 + px * w)} ${r(y0 + py * h)}`)
                     .join(' L ') + ' Z';
  }

  const f = FORMES[formeDe(z)];
  if (!f || !f.path) return null;
  return transformarPath(f.path, x0, y0, w, h);
}

/* --- Agujero de la anilla ------------------------------------------------ */
/* z.trou = { xCm, yCm, dCm, exclusionCm }  medido desde la esquina superior
   izquierda de la zona. Devuelve el circulo en px del mockup. */
function trouDe(z) {
  if (!z || !z.trou) return null;
  const pxParCmX = z.w / z.cmW, pxParCmY = z.h / z.cmH;
  const d  = (z.trou.dCm || 0.4) * pxParCmX;
  const ex = (z.trou.exclusionCm != null ? z.trou.exclusionCm : (z.trou.dCm || 0.4) * 2) * pxParCmX;
  return {
    cx: z.x + (z.trou.xCm != null ? z.trou.xCm * pxParCmX : z.w / 2),
    cy: z.y + (z.trou.yCm != null ? z.trou.yCm * pxParCmY : d),
    r: d / 2,
    rExclusion: ex / 2,
  };
}

/* --- clipPath de Fabric --------------------------------------------------- */
function clipDeForme(z) {
  const a = rotacionDe(z);
  const { cx, cy } = centroDe(z);
  let p;
  if (esRect(z)) {
    p = new fabric.Rect({ width:z.w, height:z.h,
      rx: z.radiusPx || 0, ry: z.radiusPx || 0,
      originX:'center', originY:'center', absolutePositioned:true });
  } else {
    p = new fabric.Path(pathZone(z, 1), { originX:'center', originY:'center',
                                          absolutePositioned:true });
  }
  /* Anclado por el centro: asi el angulo gira la forma sobre el centro de la
     zona, que es el mismo eje que usan aLocal() y las guias SVG. */
  p.set({ left: cx, top: cy, angle: a });
  return p;
}

/* --- Contencion ----------------------------------------------------------- */
/* Un test exacto punto-en-path usando Path2D. Se cachea por (zona, escala)
   porque majAlertes se llama en cada movimiento del dedo. */
const _cachePath2D = new Map();
function _path2D(z, escala) {
  /* Los vertices entran en la clave: dos zonas con la misma caja pero los
     puntos movidos son formas distintas, y sin esto la segunda heredaba el
     path de la primera. */
  const clave = `${z.id}|${z.x},${z.y},${z.w},${z.h}|${escala}`
              + '|' + (z.sommets ? z.sommets.join(',') : '');
  if (_cachePath2D.has(clave)) return _cachePath2D.get(clave);
  const d = pathZone(z, escala);
  const p = d ? new Path2D(d) : null;
  _cachePath2D.set(clave, p);
  return p;
}

let _ctxTest = null;
function _ctx() {
  if (!_ctxTest) _ctxTest = document.createElement('canvas').getContext('2d');
  return _ctxTest;
}

/* ¿El punto cae dentro de la forma a esa escala? */
function puntoDentro(z, x, y, escala) {
  const local = aLocal(z, x, y);
  x = local.x; y = local.y;
  if (esRect(z)) {
    const s = (escala == null) ? 1 : escala;
    const w = z.w * s, h = z.h * s;
    const x0 = z.x + (z.w - w) / 2, y0 = z.y + (z.h - h) / 2;
    return x >= x0 - 1 && y >= y0 - 1 && x <= x0 + w + 1 && y <= y0 + h + 1;
  }
  const p = _path2D(z, escala);
  return p ? _ctx().isPointInPath(p, x, y) : true;
}

/* ¿Cae dentro del area excluida por la anilla? */
function enAgujero(z, x, y) {
  const t = trouDe(z);
  if (!t) return false;
  const p = aLocal(z, x, y);
  return Math.hypot(p.x - t.cx, p.y - t.cy) <= t.rExclusion;
}

/* Esquinas de un objeto. Se prefieren las REALES (getCoords) a las del
   bounding box: con el objeto rotado, o con la zona rotada, las del bounding
   box caen fuera de la forma y disparan avisos falsos. */
/* LAS ESQUINAS DE UN TEXTO SON LAS DE LAS LETRAS, NO LAS DE SU CAJA.
   Franklin: « ¿por qué me salió este error? no entiendo ». Tenia toda la razon
   en no entenderlo: el aviso « une partie de votre design dépasse la zone »
   miraba la caja del objeto, y la caja de un textbox de Fabric NO depende de
   lo que hay escrito. Medido en su propia taza: la caja son 299 px y las
   letras de « jose » 163. Al agrandar el texto arrastrando una esquina, la
   caja se salia de la zona por los dos lados mientras las letras seguian
   holgadamente dentro — aviso rojo, y nada que recortar.
   Un aviso que salta sin motivo es peor que no tener aviso: se aprende a
   ignorarlo, y el dia que el diseno SI se sale nadie lo mira.
   Solo se estrecha en HORIZONTAL. En vertical la caja crece con la letra, asi
   que ahi no hay hueco que sobre. Si no se puede medir, se devuelve null y
   manda la caja de siempre: mejor un aviso de mas que uno de menos. */
function esquinasTexto(b) {
  if (!b || (b.type !== 'textbox' && b.type !== 'i-text' && b.type !== 'text')) return null;
  if (typeof b.getCenterPoint !== 'function') return null;

  let ancho = 0;
  try {
    if (typeof b.calcTextWidth === 'function') ancho = b.calcTextWidth();
    else if (Array.isArray(b.__lineWidths) && b.__lineWidths.length)
      ancho = Math.max.apply(null, b.__lineWidths);
  } catch (e) { return null; }

  const w = b.width || 0, h = b.height || 0;
  if (!(ancho > 0) || !(w > 0) || ancho >= w - 0.5) return null;

  /* Donde se apoyan las letras dentro de la caja. */
  const align = String(b.textAlign || 'left');
  const libre = w - ancho;
  let dx = /center/.test(align) ? libre / 2 : /right/.test(align) ? libre : 0;
  if (b.flipX) dx = libre - dx;

  const x0 = -w / 2 + dx, x1 = x0 + ancho, y0 = -h / 2, y1 = h / 2;
  const sx = b.scaleX || 1, sy = b.scaleY || 1;
  const a = (b.angle || 0) * Math.PI / 180, cs = Math.cos(a), sn = Math.sin(a);
  const c = b.getCenterPoint();
  return [[x0,y0],[x1,y0],[x1,y1],[x0,y1]].map(([x, y]) => {
    const px = x * sx, py = y * sy;
    return [c.x + px * cs - py * sn, c.y + px * sn + py * cs];
  });
}

function esquinasDe(b) {
  const t = esquinasTexto(b);
  if (t) return t;
  if (b && typeof b.getCoords === 'function') {
    try { return b.getCoords(true, true).map(p => [p.x, p.y]); } catch (e) {}
  }
  return [[b.left, b.top], [b.left + b.width, b.top],
          [b.left, b.top + b.height], [b.left + b.width, b.top + b.height]];
}

/* Devuelve { fuera, cerca } para un objeto dentro de su zona.
   `b` puede ser un objeto de Fabric (mejor) o un bounding rect. */
function evaluarObjeto(z, b, defectoRect) {
  const esq = esquinasDe(b);
  const s = facteurSecurite(z, defectoRect);
  const fuera = esq.some(([x, y]) => !puntoDentro(z, x, y, 1)) ||
                esq.some(([x, y]) => enAgujero(z, x, y));
  const cerca = !fuera && esq.some(([x, y]) => !puntoDentro(z, x, y, s));
  return { fuera, cerca };
}

function invalidarCache() { _cachePath2D.clear(); }

/* expuesto para guias y export */
const zonaRotacion = rotacionDe;
const zonaCentro = centroDe;

/* ══════════════ CALIBRACIÓN HEREDADA — LA HUELLA DE LA CAMISETA ═══════════
   Cuando se crea un producto sin medirlo, el torso arrastra la plantilla de
   fábrica: 454 px (a veces 456) para 58 cm de ancho, que son los de una
   CAMISETA. Esa pareja no es una medida de este producto: es un valor por
   defecto que nadie tomó con la cinta. Si se acepta como buena, la escala
   (7,8276 px/cm) se aplica a un cojín o a un llavero y salen números como
   « 115,2 cm » o « 42,5 cm » para un llavero de 5 cm.
   Vive AQUÍ, en el archivo compartido, para que el admin y el Studio del
   cliente juzguen con el mismo criterio. Si cada lado tuviera su copia,
   volverían a divergir el día que cambie uno de los dos. */
function calibracionHeredada(t) {
  const px = Math.round(parseFloat((t || {}).largeurPx));
  const cm = parseFloat((t || {}).largeurCm);
  return (px === 454 || px === 456) && cm === 58;
}

/* Borra de una vista la huella heredada y todo lo que se dedujo de ella.
   No inventa nada: deja el producto « sin medir », que es la verdad. */
function olvidarCalibracionHeredada(v) {
  const t = (v || {}).torso;
  if (!calibracionHeredada(t)) return false;
  delete t.largeurPx; delete t.largeurCm; delete t.pxParCm;
  (v.zones || []).forEach(z => { delete z.cmW; delete z.cmH; });
  return true;
}
